import React from 'react';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import PropTypes from 'prop-types';

import * as SelectPrimitive from '@radix-ui/react-select';

import cnMerge from '../../lib/utils';

const Select = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

const SelectTrigger = React.forwardRef(({ className, children, ...props }, ref) => (
    <SelectPrimitive.Trigger
        ref={ref}
        className={cnMerge(
            [
                'flex h-9 w-full items-center justify-between rounded-md border border-input',
                'bg-background px-3 py-2 text-sm shadow-sm',
                'ring-offset-background placeholder:text-muted-foreground',
                'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
                'disabled:cursor-not-allowed disabled:opacity-50',
                '[&>span]:line-clamp-1'
            ].join(' '),
            className
        )}
        {...props}
    >
        {children}
        <SelectPrimitive.Icon asChild>
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground opacity-50" />
        </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
));

SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

SelectTrigger.propTypes = {
    className: PropTypes.string,
    children: PropTypes.node
};

SelectTrigger.defaultProps = {
    className: undefined,
    children: null
};

const SelectScrollUpButton = React.forwardRef(({ className, ...props }, ref) => (
    <SelectPrimitive.ScrollUpButton
        ref={ref}
        className={cnMerge('flex cursor-default items-center justify-center py-1', className)}
        {...props}
    >
        <ChevronUp className="h-4 w-4" />
    </SelectPrimitive.ScrollUpButton>
));

SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

SelectScrollUpButton.propTypes = {
    className: PropTypes.string
};

SelectScrollUpButton.defaultProps = {
    className: undefined
};

const SelectScrollDownButton = React.forwardRef(({ className, ...props }, ref) => (
    <SelectPrimitive.ScrollDownButton
        ref={ref}
        className={cnMerge('flex cursor-default items-center justify-center py-1', className)}
        {...props}
    >
        <ChevronDown className="h-4 w-4" />
    </SelectPrimitive.ScrollDownButton>
));

SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;

SelectScrollDownButton.propTypes = {
    className: PropTypes.string
};

SelectScrollDownButton.defaultProps = {
    className: undefined
};

const SelectContent = React.forwardRef(({ className, children, position, ...props }, ref) => (
    <SelectPrimitive.Portal>
        <SelectPrimitive.Content
            ref={ref}
            className={cnMerge(
                [
                    'relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-md border bg-popover',
                    'text-popover-foreground shadow-md',
                    'data-[state=open]:animate-in data-[state=closed]:animate-out',
                    'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
                    'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
                    'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2',
                    'data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
                    position === 'popper' &&
                        'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1',
                    position === 'popper' &&
                        'data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1'
                ]
                    .filter(Boolean)
                    .join(' '),
                className
            )}
            position={position}
            {...props}
        >
            <SelectScrollUpButton />
            <SelectPrimitive.Viewport
                className={cnMerge(
                    'p-1',
                    position === 'popper' &&
                        'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]'
                )}
            >
                {children}
            </SelectPrimitive.Viewport>
            <SelectScrollDownButton />
        </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
));

SelectContent.displayName = SelectPrimitive.Content.displayName;

SelectContent.propTypes = {
    className: PropTypes.string,
    children: PropTypes.node,
    position: PropTypes.oneOf(['item-aligned', 'popper'])
};

SelectContent.defaultProps = {
    className: undefined,
    children: null,
    position: 'popper'
};

const SelectLabel = React.forwardRef(({ className, ...props }, ref) => (
    <SelectPrimitive.Label
        ref={ref}
        className={cnMerge('px-2 py-1.5 text-sm font-semibold', className)}
        {...props}
    />
));

SelectLabel.displayName = SelectPrimitive.Label.displayName;

SelectLabel.propTypes = {
    className: PropTypes.string
};

SelectLabel.defaultProps = {
    className: undefined
};

const SelectItem = React.forwardRef(({ className, children, ...props }, ref) => (
    <SelectPrimitive.Item
        ref={ref}
        className={cnMerge(
            [
                'relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-2 pr-8',
                'text-sm outline-none',
                'focus:bg-accent focus:text-accent-foreground',
                'data-[disabled]:pointer-events-none data-[disabled]:opacity-50'
            ].join(' '),
            className
        )}
        {...props}
    >
        <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
            <SelectPrimitive.ItemIndicator>
                <Check className="h-4 w-4" />
            </SelectPrimitive.ItemIndicator>
        </span>

        <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
));

SelectItem.displayName = SelectPrimitive.Item.displayName;

SelectItem.propTypes = {
    className: PropTypes.string,
    children: PropTypes.node
};

SelectItem.defaultProps = {
    className: undefined,
    children: null
};

const SelectSeparator = React.forwardRef(({ className, ...props }, ref) => (
    <SelectPrimitive.Separator
        ref={ref}
        className={cnMerge('-mx-1 my-1 h-px bg-muted', className)}
        {...props}
    />
));

SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

SelectSeparator.propTypes = {
    className: PropTypes.string
};

SelectSeparator.defaultProps = {
    className: undefined
};

export {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectLabel,
    SelectScrollDownButton,
    SelectScrollUpButton,
    SelectSeparator,
    SelectTrigger,
    SelectValue
};
